import { WalletOverviewDto, WalletTransactionRow } from "@/app/core/dtos/wallet.dto";
import type { ValidatePayoutBeneficiaryFailureKind } from "./validate-payout-beneficiary-error.util";
import type { ProviderBeneficiary, ProviderPayout, ProviderPayoutMethod, ProviderWallet } from "@handshake/core-types";
import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { Action, ActionReducer, createReducer, on } from "@ngrx/store";
import {
  loadBeneficiary,
  loadBeneficiaryFailure,
  loadBeneficiarySuccess,
  loadPaymentPayouts,
  loadPaymentPayoutsFailure,
  loadPaymentPayoutsSuccess,
  loadPaymentWallets,
  loadPaymentWalletsFailure,
  loadPaymentWalletsSuccess,
  loadPayoutParam,
  loadWalletOverview,
  loadWalletOverviewFailure,
  loadWalletOverviewSuccess,
  loadWalletTransactions,
  loadWalletTransactionsFailure,
  loadWalletTransactionsSuccess,
  loadedPayoutParamFailure,
  loadedPayoutParamSuccess,
  setBeneficiaryLocally,
  validatePayoutBeneficiary,
  validatePayoutBeneficiaryFailure,
  validatePayoutBeneficiarySuccess,
  resetValidatePayoutBeneficiary,
} from "./wallets.actions";
export const WALLETS_FEATURE_NAME = "WALLETS_FEATURE";

export interface WalletsState {
  walletOverview: {
    data: WalletOverviewDto;
    status: ELoadingStatus;
    error: string;
  };
  beneficiary: {
    data: ProviderBeneficiary | null;
    status: ELoadingStatus;
    error: string;
  };
  walletTransactions: {
    data: WalletTransactionRow[];
    status: ELoadingStatus;
    error: string;
  };
  paymentWallets: {
    data: ProviderWallet[];
    status: ELoadingStatus;
    error: string;
  };
  paymentPayouts: {
    data: ProviderPayout[];
    status: ELoadingStatus;
    error: string;
  };
  payoutParam: {
    data: ProviderPayoutMethod;
    status: ELoadingStatus;
    error: string;
  };
  validatePayoutBeneficiary: {
    data: any;
    status: ELoadingStatus;
    error: string;
    failureKind: ValidatePayoutBeneficiaryFailureKind | null;
  };
}

const initialState: WalletsState = {
  walletOverview: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  beneficiary: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  walletTransactions: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  paymentWallets: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  paymentPayouts: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  payoutParam: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
  },
  validatePayoutBeneficiary: {
    data: null,
    status: ELoadingStatus.idle,
    error: null,
    failureKind: null,
  },
};

let reducer: ActionReducer<any, Action>;
reducer = createReducer(
  initialState,
  on(loadWalletOverview, (state) => ({
    ...state,
    walletOverview: {
      ...state.walletOverview,
      status: ELoadingStatus.loading,
      error: null,
      data: null,
    },
  })),
  on(loadWalletOverviewSuccess, (state, { walletOverview }) => ({
    ...state,
    walletOverview: {
      ...state.walletOverview,
      data: walletOverview,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadWalletOverviewFailure, (state, { error }) => ({
    ...state,
    walletOverview: {
      ...state.walletOverview,
      status: ELoadingStatus.error,
      error,
    },
  })),
  on(loadBeneficiary, (state) => ({
    ...state,
    beneficiary: {
      ...state.beneficiary,
      status: ELoadingStatus.loading,
      error: null,
      data: null,
    },
  })),
  on(loadBeneficiarySuccess, (state, { beneficiary }) => ({
    ...state,
    beneficiary: {
      ...state.beneficiary,
      data: beneficiary,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadBeneficiaryFailure, (state, { error }) => ({
    ...state,
    beneficiary: {
      ...state.beneficiary,
      status: ELoadingStatus.error,
      error,
    },
  })),

  on(setBeneficiaryLocally, (state, { beneficiary }) => ({
    ...state,
    beneficiary: {
      ...state.beneficiary,
      data: beneficiary,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadWalletTransactions, (state) => ({
    ...state,
    walletTransactions: {
      ...state.walletTransactions,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadWalletTransactionsSuccess, (state, { walletTransactions }) => ({
    ...state,
    walletTransactions: {
      ...state.walletTransactions,
      data: walletTransactions,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadWalletTransactionsFailure, (state, { error }) => ({
    ...state,
    walletTransactions: {
      ...state.walletTransactions,
      status: ELoadingStatus.error,
      error,
    },
  })),
  on(loadPaymentWallets, (state) => ({
    ...state,
    paymentWallets: {
      ...state.paymentWallets,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadPaymentWalletsSuccess, (state, { paymentWallets }) => ({
    ...state,
    paymentWallets: {
      ...state.paymentWallets,
      data: paymentWallets,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadPaymentWalletsFailure, (state, { error }) => ({
    ...state,
    paymentWallets: {
      ...state.paymentWallets,
      status: ELoadingStatus.error,
      error,
    },
  })),
  on(loadPaymentPayouts, (state) => ({
    ...state,
    paymentPayouts: {
      ...state.paymentPayouts,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadPaymentPayoutsSuccess, (state, { paymentPayouts }) => ({
    ...state,
    paymentPayouts: {
      ...state.paymentPayouts,
      data: paymentPayouts,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadPaymentPayoutsFailure, (state, { error }) => ({
    ...state,
    paymentPayouts: {
      ...state.paymentPayouts,
      status: ELoadingStatus.error,
      error,
    },
  })),
  on(loadPayoutParam, (state) => ({
    ...state,
    payoutParam: {
      ...state.payoutParam,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadedPayoutParamSuccess, (state, { data }) => ({
    ...state,
    payoutParam: {
      ...state.payoutParam,
      data,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadedPayoutParamFailure, (state, { error }) => ({
    ...state,
    payoutParam: {
      ...state.payoutParam,
      status: ELoadingStatus.error,
      error,
    },
  })),
  on(validatePayoutBeneficiary, (state) => ({
    ...state,
    validatePayoutBeneficiary: {
      ...state.validatePayoutBeneficiary,
      status: ELoadingStatus.loading,
      error: null,
      failureKind: null,
    },
  })),
  on(validatePayoutBeneficiarySuccess, (state, { data }) => ({
    ...state,
    validatePayoutBeneficiary: {
      ...state.validatePayoutBeneficiary,
      data,
      status: ELoadingStatus.loaded,
      failureKind: null,
    },
  })),
  on(validatePayoutBeneficiaryFailure, (state, { error, failureKind }) => ({
    ...state,
    validatePayoutBeneficiary: {
      ...state.validatePayoutBeneficiary,
      status: ELoadingStatus.error,
      error,
      failureKind,
    },
  })),
  on(resetValidatePayoutBeneficiary, (state) => ({
    ...state,
    validatePayoutBeneficiary: {
      data: null,
      status: ELoadingStatus.idle,
      error: null,
      failureKind: null,
    },
  }))
);
export function walletsReducer(state: WalletsState, action: Action) {
  return reducer(state, action);
}
